import React, { useEffect } from "react";
import {
  useLazyGetUserQuery,
  useUpdateUserMutation,
  usePreviewUserAutoReportMutation,
  useSendUserAutoReportMutation,
} from "../../../store";
import AutoReportDrawer from "./AutoReportDrawer";

/**
 * Müşteri / bayi (#209) kullanıcısına giden otomatik rapor e-postası
 * (Firma → Kullanıcılar → "Otomatik Rapor"). Yalnız merkez uçlarıyla çalışır:
 * okuma `users.read`, kayıt `users.editAutoReport` ister — buton UsersTab'da
 * bu izinlerle kapılıdır (bayi bağlamında hiç gösterilmez).
 */
const UserAutoReportDrawer = ({ open, onClose, user, token, onSuccess }) => {
  const [getUser, { data: userData, isFetching, isError, error }] = useLazyGetUserQuery();
  const [updateUser, { isLoading: isSaving }] = useUpdateUserMutation();
  const [previewReport, { isLoading: isPreviewing }] = usePreviewUserAutoReportMutation();
  const [sendReport, { isLoading: isSending }] = useSendUserAutoReportMutation();

  const fullUser = userData?.data?.user;
  // Lazy sorgu önceki kullanıcının verisini tutabilir — başka kişiye ait
  // veriyi bu kullanıcının ayarı gibi göstermeyelim.
  const loadedUser = fullUser?._id === user?._id ? fullUser : null;

  useEffect(() => {
    if (open && user?._id && token) getUser({ token, id: user._id });
  }, [open, user?._id, token, getUser]);

  let status = "ready";
  let message = null;
  if (isFetching || (!loadedUser && !isError)) status = "loading";
  else if (isError || !loadedUser) {
    status = "error";
    message =
      error?.status === 403
        ? "Bu kullanıcının rapor ayarlarını görmek için yetkiniz yok."
        : "Kullanıcı bilgisi yüklenemedi.";
  } else if (!["customer", "dealer"].includes(loadedUser.role)) {
    status = "unavailable";
    message = "Otomatik rapor yalnızca müşteri ve bayi kullanıcıları için ayarlanabilir.";
  }

  const userName = [user?.name, user?.lastName].filter(Boolean).join(" ");

  const handleSave = async (config) => {
    await updateUser({
      input: {
        id: loadedUser._id,
        name: loadedUser.name,
        lastName: loadedUser.lastName,
        tel: loadedUser.tel,
        role: loadedUser.role,
        firmName: loadedUser.firm?.firmName ?? "",
        subRole: loadedUser.subRole ?? "",
        cardId: loadedUser.cardId ?? "",
        isActive: loadedUser.isActive !== false,
        autoReportMailConfig: config,
      },
      token,
    }).unwrap();
    onSuccess?.();
  };

  const handlePreview = async (frequency, cfg) => {
    const resp = await previewReport({
      id: loadedUser._id,
      frequency,
      coverage: cfg?.coverage,
      sections: cfg?.sections,
      token,
    }).unwrap();
    return resp?.data;
  };

  const handleSend = (frequency, cfg) =>
    sendReport({
      id: loadedUser._id,
      frequency,
      coverage: cfg?.coverage,
      sections: cfg?.sections,
      token,
    }).unwrap();

  return (
    <AutoReportDrawer
      open={open}
      onClose={onClose}
      title="Otomatik Rapor"
      subtitle={userName}
      status={status}
      message={message}
      recipient={
        <>
          Açık olan raporlar seçilen zamanda PDF olarak{" "}
          {loadedUser?.email ? (
            <><span className="font-SemiBold text-gray-800">{loadedUser.email}</span> adresine</>
          ) : (
            "kullanıcının e-posta adresine"
          )}{" "}
          gönderilir.
        </>
      }
      savedConfig={loadedUser?.autoReportMailConfig}
      onSave={handleSave}
      onPreview={handlePreview}
      onSend={handleSend}
      isSaving={isSaving}
      isPreviewing={isPreviewing}
      isSending={isSending}
    />
  );
};

export default UserAutoReportDrawer;
